import * as THREE from 'three';
import { BLACK, basic, cloth, emis, leather, std, stone as stoneMat } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Hondero trasgo (docs/CRIATURAS.md §2): the era I ranged nightmare. A lean, twitchy cousin of the shadow goblin
 * (same ears, claws and underbite) under a ragged ash hood stuck with crow feathers; its right arm is always up,
 * whirling a sling over its head with an ember-hot stone in the pouch, and a bag of stones and coals hangs at its
 * left hip. The sling (cords and pouch) is its own bone pivoting at the fist so the clips spin it; the stone and
 * its fiery streak are bones of their own so the attack can let the stone go (see anim/creatures/slinger.ts).
 * Opaque shared materials; the ember is its one self-lit material (stone, coals, eyes) and the shadow smoke its
 * one translucent one, plus a few glow sprites.
 */

/** Sling radius (fist to pouch). */
const SR = 0.42;
const HAND = V(0.27, 1.6, 0.1);

/**
 * The sling (model coordinates): the fist it whirls from and the pouch at rest (out to the right of the fist, where
 * it swings forward), and the stone's place at the moment of release in the attack clip.
 */
export const SLINGER = {
  hand: HAND,
  pouch: V(HAND.x + SR, HAND.y - 0.05, HAND.z),
  radius: SR,
  /** Where the stone actually is when the attack clip lets it go (0.13 s, mid-lunge): spawn the projectile here. */
  release: V(0.61, 1.1, 0.89),
};

/** A ragged open cone (hood mantles, loincloths): the hem torn into points. */
const ragged = (rTop: number, rBot: number, h: number, seg: number, tear: number, rng: () => number, theta?: [number, number]): THREE.BufferGeometry => {
  const geo = theta ? new THREE.CylinderGeometry(rTop, rBot, h, seg, 2, true, theta[0], theta[1]) : new THREE.CylinderGeometry(rTop, rBot, h, seg, 2, true);
  const p = geo.attributes.position as THREE.BufferAttribute;
  const cuts = new Map<number, number>();
  for (let i = 0; i < p.count; i++) {
    if (p.getY(i) > -h / 2 + 0.001) continue;
    const key = Math.round(Math.atan2(p.getX(i), p.getZ(i)) * 1000);
    let cut = cuts.get(key);
    if (cut === undefined) {
      cut = cuts.size % 2 ? -tear * (0.6 + rng() * 0.6) : tear * rng() * 0.4;
      cuts.set(key, cut);
    }
    p.setY(i, p.getY(i) + cut);
  }
  geo.computeVertexNormals();
  return geo;
};

/**
 * Bones: body at the hips (pelvis, belt, loincloth; the bag of stones swings on its own `bag` pivot), chest above it
 * (the hunched trunk, hood mantle, smoke) carrying the head (jaw hinged under it) and both arms; the right forearm
 * holds the `sling`, which carries the `stone` and, behind it, the `trail` of fire it leaves when whirled fast;
 * the legs bend at the knee.
 */
export const slinger = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 43);
  const { g, R, rng } = c;
  const hide = std('#2b2537', { roughness: 0.62, bumpScale: 0.03 });
  const hood = cloth('#55515a', { side: THREE.DoubleSide, bumpScale: 0.035 });
  const rag = cloth('#26232b', { side: THREE.DoubleSide, bumpScale: 0.03 });
  const strap = leather('#2a201c');
  const cord = std('#8e8994', { roughness: 0.9 });
  const claw = std('#c4beb0', { roughness: 0.6 });
  const rock = stoneMat('#55525a');
  const ink = std('#0d0b12', { roughness: 0.3 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.8);

  const SH = [-1, 1].map((k) => V(k * 0.2, 0.98, 0.06));
  const EL = [V(-0.33, 0.74, 0.18), V(0.4, 1.3, 0.14)];
  const WR = [V(-0.27, 0.52, 0.27), HAND];
  const bodyJ = joint(g, V(0, 0.55, 0), 'body');
  const bagJ = joint(bodyJ.abs, V(-0.21, 0.66, 0.06), 'bag');
  const chestJ = joint(bodyJ.abs, V(0, 0.68, 0.0), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.04, 0.12), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.06, 0.24), 'jaw');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const slingJ = joint(foreJ[1]!.abs, HAND, 'sling');
  const P = SLINGER.pouch;
  const stoneJ = joint(slingJ.abs, P, 'stone');
  const trailJ = joint(slingJ.abs, P, 'trail');
  const legJ = [-1, 1].map((k) => joint(g, V(k * 0.11, 0.55, 0), k < 0 ? 'legL' : 'legR'));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, V(k * 0.17, 0.3, 0.13), k < 0 ? 'shinL' : 'shinR'));

  // Legs: thin and bowed, long shins on splayed clawed feet.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const hip = V(k * 0.11, 0.55, 0), knee = V(k * 0.17, 0.3, 0.13), ank = V(k * 0.14, 0.06, -0.02);
    limb(legJ[i]!.abs, hip, knee, 0.06, 0.045, hide, 6);
    limb(shinJ[i]!.abs, knee, ank, 0.042, 0.03, hide, 6);
    const ft = M(new THREE.BoxGeometry(0.08, 0.04, 0.17), hide, shinJ[i]!.abs);
    ft.position.set(k * 0.15, 0.03, 0.04);
    ft.rotation.y = k * 0.2;
    for (let f = 0; f < 2; f++) {
      const cl = M(new THREE.ConeGeometry(0.012, 0.06, 4, 1, true), claw, shinJ[i]!.abs);
      cl.position.set(k * 0.15 + (f - 0.5) * 0.04 + k * 0.02, 0.02, 0.14);
      cl.rotation.x = Math.PI / 2;
    }
    // Rag wrappings round the shin.
    const wr = M(new THREE.CylinderGeometry(0.046, 0.05, 0.08, 6, 1, true), rag, shinJ[i]!.abs);
    wr.position.set(k * 0.155, 0.16, 0.04);
    wr.rotation.x = -0.45;
  }

  // Pelvis, a rope belt and a ragged loincloth front and back.
  const bd = bodyJ.abs;
  const pel = M(new THREE.SphereGeometry(0.14, 7, 4), hide, bd);
  pel.scale.set(1.15, 0.8, 0.95);
  pel.position.set(0, 0.62, 0.02);
  const belt = M(new THREE.TorusGeometry(0.15, 0.018, 3, 8), strap, bd);
  belt.scale.set(1.08, 1, 0.95);
  belt.position.set(0, 0.66, 0.03);
  belt.rotation.x = Math.PI / 2 + 0.15;
  for (const [a0, len] of [[Math.PI * 0.25, Math.PI * 0.5], [Math.PI * 1.2, Math.PI * 0.6]] as const) {
    const lf = M(ragged(0.15, 0.2, 0.28, 4, 0.1, rng, [a0, len]), rag, bd);
    lf.position.set(0, 0.54, 0.02);
  }

  // The bag of stones on its strap at the left hip: a lumpy sack, its mouth open on stones and a few live coals.
  const bg = bagJ.abs;
  const sack = M(new THREE.SphereGeometry(0.1, 6, 4), strap, bg);
  sack.scale.set(0.95, 1.1, 0.75);
  sack.position.set(-0.27, 0.53, 0.07);
  const lip = M(new THREE.TorusGeometry(0.06, 0.014, 3, 6), strap, bg);
  lip.position.set(-0.28, 0.63, 0.08);
  lip.rotation.set(Math.PI / 2 - 0.35, 0, 0.25);
  for (let i = 0; i < 3; i++) {
    const s = M(new THREE.IcosahedronGeometry(0.03 + rng() * 0.01, 0), i === 1 ? ember : rock, bg);
    s.position.set(-0.28 + Math.cos(i * 1.7) * 0.03, 0.645 + rng() * 0.02, 0.08 + Math.sin(i * 1.7) * 0.025);
    s.rotation.set(R() * 3, R() * 3, 0);
  }
  glowSprite(bg, '#ff6a3a', 0.2, V(-0.28, 0.67, 0.09), 0.55);
  limb(bg, V(-0.22, 0.62, 0.1), V(-0.21, 0.67, 0.07), 0.012, 0.012, strap, 3);

  // The trunk: lean and hunched, a knobbed spine, a sling-bag strap across the chest.
  const torso = M(new THREE.SphereGeometry(0.2, 9, 6), hide, ch);
  torso.scale.set(1, 1.3, 0.82);
  torso.position.set(0, 0.86, 0.04);
  torso.rotation.x = 0.45;
  const belly = M(new THREE.SphereGeometry(0.13, 6, 4), hide, ch);
  belly.position.set(0, 0.74, 0.1);
  for (let i = 0; i < 4; i++) {
    const kn = M(new THREE.ConeGeometry(0.022, 0.06, 4, 1, true), hide, ch);
    kn.position.set(0, 0.8 + i * 0.075, -0.11 - i * 0.022);
    kn.rotation.x = -1.2 + i * 0.1;
  }
  const band = new THREE.CatmullRomCurve3([V(0.17, 1.0, 0.04), V(0.06, 0.92, 0.2), V(-0.12, 0.74, 0.17), V(-0.21, 0.66, 0.08)]);
  M(new THREE.TubeGeometry(band, 6, 0.014, 3), strap, ch);

  // The hood: an ash cowl over the head with a mantle to the shoulders torn into points, a long tail behind.
  const mantle = M(ragged(0.12, 0.25, 0.24, 9, 0.13, rng), hood, ch);
  mantle.position.set(0, 0.95, 0.02);
  mantle.rotation.x = 0.35;

  // The head: the goblin's, sharper; long ears through slits in the hood, a hooked nose, eyes like coals.
  const hd = headJ.abs;
  const skull = M(new THREE.SphereGeometry(0.14, 9, 6), hide, hd);
  skull.position.set(0, 1.12, 0.21);
  skull.scale.set(1, 0.9, 1.12);
  const cowl = M(new THREE.SphereGeometry(0.168, 9, 6, Math.PI / 2 + 0.8, Math.PI * 2 - 1.6, 0, Math.PI / 1.45), hood, hd);
  cowl.position.set(0, 1.14, 0.18);
  cowl.rotation.x = -0.35;
  cowl.scale.set(1, 1.08, 1.05);
  const tail = M(new THREE.ConeGeometry(0.08, 0.36, 6, 1, true), hood, hd);
  tail.position.set(0, 1.2, -0.08);
  tail.rotation.x = -2.1;
  M(new THREE.SphereGeometry(0.12, 6, 4), BLACK, hd).position.set(0, 1.13, 0.2);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.045, 0.3, 5), hide, hd);
    ear.position.set(k * 0.24, 1.17, 0.15);
    ear.rotation.set(-0.2, 0, -k * 1.2);
    ear.scale.z = 0.55;
    // Coal eyes, slanted, deep under the brow.
    const e = M(new THREE.SphereGeometry(0.019, 5, 4), ember, hd);
    e.position.set(k * 0.052, 1.14, 0.33);
    e.scale.set(1.4, 0.7, 0.8);
    e.rotation.z = -k * 0.35;
    glowSprite(hd, '#ff5a36', 0.11, V(k * 0.052, 1.14, 0.345), 0.85);
    const brow = M(new THREE.BoxGeometry(0.06, 0.016, 0.03), hide, hd);
    brow.position.set(k * 0.05, 1.165, 0.33);
    brow.rotation.z = k * 0.4;
  }
  const nose = M(new THREE.ConeGeometry(0.028, 0.14, 5), hide, hd);
  nose.position.set(0, 1.09, 0.38);
  nose.rotation.x = 1.95;
  // Crow feathers stuck in the hood's band, and one bone bead.
  // A crest of crow feathers fanned up behind the hood, bound on with a bone bead: its crown.
  for (let i = 0; i < 5; i++) {
    const a = (i - 2) * 0.32;
    const len = 0.3 - Math.abs(i - 2) * 0.05;
    const f = M(new THREE.ConeGeometry(0.032, len, 4, 1, true), ink, hd);
    f.scale.z = 0.28;
    const base = V(Math.sin(a) * 0.05, 1.27, 0.06 - Math.abs(i - 2) * 0.01);
    const dir = V(Math.sin(a) * 0.9, Math.cos(a), -0.55).normalize();
    f.position.copy(base).addScaledVector(dir, len / 2);
    f.quaternion.setFromUnitVectors(V(0, 1, 0), dir);
  }
  M(new THREE.SphereGeometry(0.02, 5, 3), claw, hd).position.set(0, 1.285, 0.05);
  // The jaw: an underbite of crooked fangs.
  const jw = jawJ.abs;
  M(new THREE.BoxGeometry(0.15, 0.045, 0.1), hide, jw).position.set(0, 1.03, 0.29);
  for (let i = 0; i < 4; i++) {
    const tooth = M(new THREE.ConeGeometry(0.011, 0.04, 4, 1, true), claw, jw);
    tooth.position.set(-0.05 + i * 0.033 + R() * 0.008, 1.065, 0.33);
    tooth.rotation.z = R() * 0.3;
  }

  // Arms: long and wiry, knobbed elbows, three claws to a hand.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const a = armJ[i]!.abs, f = foreJ[i]!.abs;
    limb(a, SH[i]!, EL[i]!, 0.05, 0.036, hide, 6);
    M(new THREE.SphereGeometry(0.036, 5, 3), hide, f).position.copy(EL[i]!);
    limb(f, EL[i]!, WR[i]!, 0.032, 0.026, hide, 6);
    M(new THREE.SphereGeometry(0.036, 6, 4), hide, f).position.copy(WR[i]!);
  }
  // Left hand: claws hooked, hanging over the bag.
  for (let cI = 0; cI < 3; cI++) {
    const cl = M(new THREE.ConeGeometry(0.011, 0.065, 4, 1, true), claw, foreJ[0]!.abs);
    cl.position.set(WR[0]!.x + (cI - 1) * 0.022, WR[0]!.y - 0.045, WR[0]!.z + 0.025);
    cl.rotation.x = Math.PI - 0.5;
  }
  // Right fist: knuckles closed on the cords, a rag wound round the wrist.
  for (let cI = 0; cI < 3; cI++) {
    const cl = M(new THREE.ConeGeometry(0.01, 0.05, 4, 1, true), claw, foreJ[1]!.abs);
    cl.position.set(HAND.x - 0.03, HAND.y + 0.005 - cI * 0.02, HAND.z + 0.03);
    cl.rotation.z = 1.9;
  }
  const wrap = M(new THREE.CylinderGeometry(0.034, 0.036, 0.07, 6, 1, true), rag, foreJ[1]!.abs);
  wrap.position.set(0.34, 1.45, 0.12);
  wrap.rotation.z = 0.46;

  // The sling: two cords from the fist to a leather pouch cupping the stone (the clips spin it round the fist).
  const sl = slingJ.abs;
  const H2 = V(HAND.x + 0.02, HAND.y + 0.01, HAND.z);
  for (const k of [-1, 1]) {
    const mid = V(HAND.x + SR * 0.55, HAND.y - 0.025, HAND.z + k * 0.012);
    limb(sl, H2, mid, 0.008, 0.008, cord, 3);
    limb(sl, mid, V(P.x - 0.06, P.y, P.z + k * 0.04), 0.008, 0.008, cord, 3);
  }
  const pouch = M(new THREE.SphereGeometry(0.075, 7, 3, 0, Math.PI * 2, Math.PI / 2 - 0.3, Math.PI / 2 + 0.3), strap, sl);
  pouch.scale.set(1.25, 0.75, 0.95);
  pouch.position.copy(P);
  M(new THREE.SphereGeometry(0.012, 4, 3), strap, sl).position.copy(H2);

  // The stone: a lump of ember-hot rock in the pouch.
  const st = M(new THREE.IcosahedronGeometry(0.062, 0), ember, stoneJ.abs);
  st.position.copy(P).setY(P.y + 0.025);
  st.rotation.set(0.4, 0.7, 0.2);

  // The streak it leaves behind when whirled: a tapering tube of fire along the circle (the clips scale it).
  const pts: THREE.Vector3[] = [];
  for (let j = 0; j <= 8; j++) {
    const a = (j / 8) * 2.5;
    pts.push(V(HAND.x + Math.cos(a) * SR, HAND.y - 0.03 + 0.02 * (j / 8), HAND.z - Math.sin(a) * SR));
  }
  const curve = new THREE.CatmullRomCurve3(pts);
  const segs = 12, rad = 3;
  const tg = new THREE.TubeGeometry(curve, segs, 0.04, rad, false);
  const tp = tg.attributes.position as THREE.BufferAttribute;
  const ctr = new THREE.Vector3(), q = new THREE.Vector3();
  for (let i = 0; i < tp.count; i++) {
    const ring = Math.floor(i / (rad + 1));
    const k = ring / segs;
    curve.getPointAt(k, ctr);
    q.fromBufferAttribute(tp, i).sub(ctr).multiplyScalar((1 - k) ** 1.3);
    tp.setXYZ(i, ctr.x + q.x, ctr.y + q.y, ctr.z + q.z);
  }
  M(tg, ember, trailJ.abs);

  // Shadow smoke curling off the shoulders and back, as off its kin.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.45);
  for (let i = 0; i < 2; i++) {
    const a = 2.9 + i * 1.6;
    ribs.push(new Ribbon(ch, wisp(Math.cos(a) * 0.14, Math.sin(a) * 0.12 - 0.04, 0.7, 5).map((p) => p.add(V(0, 0.72, 0))), 0.026, wm, rng, 0.045));
  }
  glowSprite(G(ch), '#5a3cff', 0.8, V(0, 0.85, 0.05), 0.1);

  // Only the smoke boils here: the whirl, the throw and the scuttle are the clips'.
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  t();
  return done(c, 1.6, t, undefined, bonesOf(c, {
    body: bodyJ.p, bag: bagJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, sling: slingJ.p, stone: stoneJ.p, trail: trailJ.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
  }));
};
